<template>
  <form class="report-period" @submit.prevent="apply">
    <div class="period-preset">
      <label :for="id">Period</label><select :id="id" v-model="preset" @change="selectPreset">
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="month">This month</option>
        <option value="last-month">Last month</option>
        <option value="custom">Custom dates</option>
      </select>
    </div>
    <div class="date-input">
      <label :for="id + '-start'">From</label><FlatPickr :id="id + '-start'" v-model="start" :config="startConfig" required @on-change="preset = 'custom'" />
    </div>
    <div class="date-input">
      <label :for="id + '-end'">To</label><FlatPickr :id="id + '-end'" v-model="end" :config="endConfig" required @on-change="preset = 'custom'" />
    </div>
    <button type="submit" :disabled="!start || !end || start > end || !changed">Apply</button>
  </form>
</template>
<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import moment from 'moment'
import FlatPickr from 'vue-flatpickr-component'
import type { Options } from 'flatpickr/dist/types/options'
import 'flatpickr/dist/flatpickr.css'
import './report-calendar.css'
const props = defineProps<{ id: string; value: string }>()
const emit = defineEmits<{ 'update:value': [value: string] }>()
const preset = ref('30'),
  start = ref(''),
  end = ref('')
watch(
  () => props.value,
  (value) => {
    ;[start.value, end.value] = value.split(' - ') as [string, string]
  },
  { immediate: true },
)
const calendarConfig: Options = {
  dateFormat: 'Y-m-d',
  disableMobile: true,
  locale: { firstDayOfWeek: 1 },
  monthSelectorType: 'static',
  onReady: (_dates, _value, instance) => {
    instance.calendarContainer.classList.add('pacsort-calendar')
  },
}
const startConfig = computed<Options>(() => ({ ...calendarConfig, maxDate: end.value || undefined }))
const endConfig = computed<Options>(() => ({ ...calendarConfig, minDate: start.value || undefined }))
const changed = computed(() => `${start.value} - ${end.value}` !== props.value)
function apply() {
  if (start.value && end.value && start.value <= end.value) emit('update:value', `${start.value} - ${end.value}`)
}
function selectPreset() {
  if (preset.value === 'custom') return
  let from = moment(),
    to = moment()
  if (preset.value === 'yesterday') {
    from = moment().subtract(1, 'day')
    to = from.clone()
  } else if (preset.value === 'month') from = moment().startOf('month')
  else if (preset.value === 'last-month') {
    from = moment().subtract(1, 'month').startOf('month')
    to = from.clone().endOf('month')
  } else if (preset.value !== 'today') from = moment().subtract(Number(preset.value) - 1, 'days')
  start.value = from.format('YYYY-MM-DD')
  end.value = to.format('YYYY-MM-DD')
  apply()
}
</script>
<style scoped>
.report-period {
  display: flex;
  align-items: end;
  gap: 12px;
  flex-wrap: wrap;
}
.report-period > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
label {
  font-size: 12px;
  font-weight: 400;
  color: #62766c;
}
:deep(input),
select,
button {
  font: inherit;
  font-size: 14px;
  min-height: 42px;
  border: 1px solid #d5e1da;
  border-radius: 6px;
  padding: 8px 10px;
  background: white;
  color: #204638;
}
button {
  background: #164f40;
  color: white;
  padding: 8px 18px;
  cursor: pointer;
}
button:disabled {
  background: #eef2ef;
  color: #8c9992;
  cursor: default;
}
:deep(input:focus-visible),
select:focus-visible,
button:focus-visible {
  outline: 3px solid #f6946b;
  outline-offset: 2px;
}
@media (max-width: 600px) {
  .report-period {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    width: 100%;
    gap: 10px;
  }
  .period-preset {
    grid-column: 1/-1;
  }
  :deep(input),
  select,
  button {
    width: 100%;
    min-width: 0;
    min-height: 44px;
  }
  .report-period button {
    grid-column: 1/-1;
  }
}
</style>

<style scoped>
.date-input :deep(input) {
  width: 146px;
  cursor: pointer;
  padding-right: 12px;
  background: #fff;
  color: #204638;
}
@media (max-width: 600px) {
  .date-input :deep(input) {
    width: 100%;
    min-width: 0;
  }
}
</style>
